export interface LocationData {
  slug: string;
  name: string;
  state: string;
  /** County / regional grouping shown under the town name */
  region: string;
  /** Approximate driving distance from the Wakefield shop */
  distanceMiles: number;
  isPrimary: boolean;
  lat: number;
  lng: number;
  /** 145–155 char meta description used by the SEO head manager */
  metaDescription: string;
  description: string;
  highlights: string[];
  content: string[];
  /** Real neighbourhoods and village areas inside the town */
  nearbyAreas: string[];
}

/**
 * Every town below is a real municipality inside NIMAR's 30-mile service
 * radius, measured from the Wakefield, MA shop at 73 Chestnut St.
 */
export const locations: LocationData[] = [
  {
    slug: "wakefield-ma",
    name: "Wakefield",
    state: "MA",
    region: "Middlesex County",
    distanceMiles: 0,
    isPrimary: true,
    lat: 42.5057,
    lng: -71.0723,
    metaDescription:
      "Landscaping and masonry in Wakefield MA. Local crews on Chestnut Street handling lawn care, patios, walls, repointing and foundation repair. Free estimates.",
    description:
      "Wakefield is home. Our shop sits on Chestnut Street, which means the shortest response times, the most flexible scheduling and crews who already know your street.",
    highlights: [
      "Home base — fastest scheduling in town",
      "Weekly and bi-weekly maintenance routes",
      "Patios, walkways and retaining walls",
      "Repointing and foundation repair on older homes",
    ],
    content: [
      "NIMAR Landscaping and Masonry is based at 73 Chestnut St in Wakefield, and the town accounts for more of our work than anywhere else on our route. Being local matters more than it sounds: when a spring storm drops limbs across the neighbourhood or a client needs a step reset before a family event, we are minutes away rather than an hour out.",
      "Wakefield housing stock runs from tight Victorian lots near the Junction to larger properties out toward Montrose and the Greenwood line. That mix keeps us doing both sides of the business in the same week — weekly lawn maintenance and bed work on one street, chimney repointing and foundation repair on hundred-year-old masonry two streets over.",
      "The neighbourhoods around Lake Quannapowitt bring their own conditions. Properties closer to the water hold moisture longer, which changes how we schedule mowing, how deep we build patio base and how aggressively we detail drainage behind a retaining wall. We plan for it rather than discovering it in the second wet spring.",
    ],
    nearbyAreas: ["Greenwood", "Montrose", "West Side", "Lake Quannapowitt", "The Junction", "Woodville"],
  },
  {
    slug: "reading-ma",
    name: "Reading",
    state: "MA",
    region: "Middlesex County",
    distanceMiles: 3,
    isPrimary: false,
    lat: 42.5257,
    lng: -71.0953,
    metaDescription:
      "Landscaping and masonry in Reading MA. Weekly lawn maintenance, paver patios, retaining walls and brick repointing. Free written on-site estimates.",
    description:
      "Three miles from our shop, Reading is one of our busiest weekly maintenance towns and a steady source of hardscape and repointing work.",
    highlights: [
      "Three miles from our Wakefield shop",
      "Established weekly maintenance routes",
      "Paver patios, walkways and steps",
      "Chimney and brick repointing",
    ],
    content: [
      "Reading sits three miles from our shop, which puts it on our earliest maintenance routes of the week. Crews are on site early, which matters in July when the useful working hours are the ones before noon.",
      "The mature tree canopy across much of Reading is beautiful and hard on turf. Shaded lawns need a different mowing height, different seed mix and a different feeding schedule than a lawn in full sun, and root competition from established oaks and maples means fertilisation programmes need adjusting rather than copying from the house next door.",
      "On the masonry side, Reading's older housing stock keeps us busy with front step rebuilds, chimney crowns and repointing. Many of these homes were built with soft historic mortar, and patching them with a modern high-strength mix causes more damage than it fixes — we match the original mortar so the brick stays the strong part of the wall.",
    ],
    nearbyAreas: ["Downtown Reading", "Birch Meadow", "Reading Highlands", "Bear Hill", "Wood End"],
  },
  {
    slug: "north-reading-ma",
    name: "North Reading",
    state: "MA",
    region: "Middlesex County",
    distanceMiles: 6,
    isPrimary: false,
    lat: 42.5751,
    lng: -71.0787,
    metaDescription:
      "Landscaping and masonry in North Reading MA. Large-lot landscape design, irrigation, patios and stone walls from a local Wakefield crew. Free estimates.",
    description:
      "North Reading's larger lots suit full landscape design work — full-property planning, irrigation and hardscaping rather than piecemeal fixes.",
    highlights: [
      "Large-property landscape design and installation",
      "Irrigation installation, repair and winterizing",
      "Retaining walls and grade management",
      "Full-service seasonal maintenance",
    ],
    content: [
      "North Reading properties tend to be larger than the tight lots closer to Boston, and that changes the kind of work we do here. Instead of squeezing a patio into a small yard, we are usually planning across the whole property — circulation between spaces, planting zones, irrigation coverage and where the grade needs to be corrected.",
      "Bigger lots also mean irrigation matters more. Hand watering an acre is not realistic, so we design zoned systems that match how different parts of the property dry out — full-sun turf on one schedule, shaded beds on another, drip lines through the planted areas.",
      "The rolling grade in parts of town, particularly out toward Martins Pond, means retaining walls come up regularly. Every wall we build here gets clean drainage stone, filter fabric and a real drain outlet behind the face, because a wall holding wet New England soil without drainage is only waiting for the right spring.",
    ],
    nearbyAreas: ["Martins Pond", "Berry Center", "Park Street", "Swan Pond", "Central Village"],
  },
  {
    slug: "stoneham-ma",
    name: "Stoneham",
    state: "MA",
    region: "Middlesex County",
    distanceMiles: 3,
    isPrimary: false,
    lat: 42.4801,
    lng: -71.0995,
    metaDescription:
      "Landscaping and masonry in Stoneham MA. Retaining walls, drainage correction, paver patios and lawn care near the Middlesex Fells. Free estimates.",
    description:
      "Stoneham's ledge and slope conditions near the Fells make it retaining-wall and drainage country — work we do constantly.",
    highlights: [
      "Retaining walls on sloped and ledge lots",
      "Drainage correction and regrading",
      "Paver patios and walkways",
      "Weekly and bi-weekly lawn maintenance",
    ],
    content: [
      "Stoneham sits right up against the Middlesex Fells, and the terrain shows it. Ledge close to the surface and properties that fall away from the street are common, which means a lot of what we do here involves holding grade and moving water rather than simply planting and mowing.",
      "Retaining walls are the obvious answer, but the wall itself is the visible half of the job. The half that determines whether it is still straight in fifteen years is the compacted base course, the drainage stone behind the face and the drain line carrying water somewhere it can actually go.",
      "We also see a steady stream of foundation work in Stoneham. Homes built into a slope get more water against the foundation wall than homes on flat lots, so we repair from both sides — sealing from the inside and correcting exterior grade and downspout discharge so water stops arriving at the wall in the first place.",
    ],
    nearbyAreas: ["Stoneham Square", "Spot Pond", "Farm Hill", "Bear Hill", "Middlesex Fells Edge"],
  },
  {
    slug: "woburn-ma",
    name: "Woburn",
    state: "MA",
    region: "Middlesex County",
    distanceMiles: 6,
    isPrimary: false,
    lat: 42.4793,
    lng: -71.1523,
    metaDescription:
      "Landscaping and masonry in Woburn MA. Residential and commercial grounds care, paver driveways, walls and masonry repair. Free on-site estimates.",
    description:
      "Woburn's mix of dense residential streets and commercial property means we run both homeowner maintenance and commercial grounds work here.",
    highlights: [
      "Residential and commercial grounds maintenance",
      "Paver driveways and parking aprons",
      "Masonry repair, repointing and steps",
      "Spring and fall clean-ups with full haul-away",
    ],
    content: [
      "Woburn is one of the more varied towns on our route. In a single week we might run weekly maintenance on residential streets around Horn Pond, install a paver driveway in North Woburn and repoint a commercial building's brick facade near Four Corners.",
      "Driveway work comes up often here, and a driveway is not a patio with cars on it. It gets deeper base, heavier pavers and an interlocking pattern that spreads vehicle load. We also set the field flush and even so a plow blade rides across it in February instead of catching an edge and lifting a course.",
      "For commercial clients the priority is usually schedule reliability rather than design. Maintenance visits happen on the same day each cycle, hard surfaces are blown clean before the crew leaves, and clean-up debris is taken with us — a tidy site matters more when customers are walking across it every day.",
    ],
    nearbyAreas: ["Four Corners", "Montvale", "North Woburn", "Central Square", "Horn Pond", "Cummings Park"],
  },
  {
    slug: "lynnfield-ma",
    name: "Lynnfield",
    state: "MA",
    region: "Essex County",
    distanceMiles: 4,
    isPrimary: false,
    lat: 42.539,
    lng: -71.0448,
    metaDescription:
      "Landscaping and masonry in Lynnfield MA. Full-property landscape design, natural stone walls, patios and landscape lighting. Free on-site estimates.",
    description:
      "Lynnfield clients tend to want the whole property done to one standard — design, hardscape, lighting and maintenance under one crew.",
    highlights: [
      "Full-property landscape design and installation",
      "Natural stone walls, patios and steps",
      "Low-voltage landscape lighting",
      "Detail-level weekly maintenance",
    ],
    content: [
      "Lynnfield properties are generally larger and better established than average for our area, and expectations follow. Clients here are usually less interested in a quick fix and more interested in getting the whole property to a consistent standard — which suits the way we prefer to work.",
      "That typically means starting with a design rather than a single project. We map the property first, then build it in one run or across a couple of seasons: hardscape and grade work first, planting second, lighting last so fixtures land where the finished landscape actually needs them.",
      "Natural stone comes up more here than block. Stone costs more and takes longer to lay well, but on an established Lynnfield property it is usually the right call — it ages into the landscape rather than looking newer every year as the plantings mature around it.",
    ],
    nearbyAreas: ["Lynnfield Center", "South Lynnfield", "Pillings Pond", "Sagamore", "Kings Lane"],
  },
  {
    slug: "saugus-ma",
    name: "Saugus",
    state: "MA",
    region: "Essex County",
    distanceMiles: 6,
    isPrimary: false,
    lat: 42.4654,
    lng: -71.0101,
    metaDescription:
      "Landscaping and masonry in Saugus MA. Lawn maintenance, paver walkways, front step rebuilds and foundation repair. Free written on-site estimates.",
    description:
      "Saugus keeps us doing practical, high-value work: front walks, steps, foundations and reliable seasonal maintenance.",
    highlights: [
      "Front walkway and entry step rebuilds",
      "Foundation crack and parge repair",
      "Weekly and bi-weekly lawn maintenance",
      "Spring and fall clean-ups",
    ],
    content: [
      "A lot of our Saugus work is the kind that pays for itself in curb appeal. Front walks that have settled into a wave, entry steps with uneven risers and a foundation face that looks tired can make an otherwise well-kept house read as neglected from the street.",
      "Settled steps and walks almost always trace back to the same cause: a base that never went below frost depth. Resetting the surface without fixing that only buys a couple of winters, so we rebuild the base properly, bring every riser to a consistent height and pitch treads so water runs off instead of freezing on them.",
      "On the maintenance side, Saugus properties near the marsh and lower-lying areas hold moisture, which affects both mowing schedules and how we handle drainage around patios and walls. We adjust the cut and the base detailing rather than treating every property the same.",
    ],
    nearbyAreas: ["Cliftondale", "East Saugus", "Golden Hills", "Saugus Center", "Lynnhurst", "Oaklandvale"],
  },
  {
    slug: "malden-ma",
    name: "Malden",
    state: "MA",
    region: "Middlesex County",
    distanceMiles: 7,
    isPrimary: false,
    lat: 42.4251,
    lng: -71.0662,
    metaDescription:
      "Landscaping and masonry in Malden MA. Small-yard design, brick repointing, front steps and compact retaining walls for dense city lots. Free estimates.",
    description:
      "Malden's tight urban lots and older brick housing stock mean small-space design and serious masonry repair in equal measure.",
    highlights: [
      "Small-yard and courtyard design",
      "Brick repointing and facade repair",
      "Front steps, landings and railings",
      "Compact patios and privacy planting",
    ],
    content: [
      "Malden lots are tight, and a tight lot is harder to design well than a big one — every square foot has to earn its place. We work a lot of small back yards here into genuinely usable space: a compact patio, a seating wall doing double duty as a retaining wall, and layered planting that creates privacy without eating the whole yard.",
      "The brick and stone housing stock across Malden generates constant masonry work. Receding mortar joints, deteriorated chimney crowns and spalling brick faces are all common in buildings of this age, and mortar is meant to be the sacrificial layer — once it recedes past a certain point, water gets behind the face and the brick itself starts breaking apart.",
      "Access is part of every quote here. Narrow driveways, shared alleys and street parking mean materials often have to be moved by hand or staged in stages, and we plan for that rather than surprising you with it mid-project.",
    ],
    nearbyAreas: ["Maplewood", "Edgeworth", "Linden", "Faulkner", "Forestdale", "Oak Grove"],
  },
  {
    slug: "melrose-ma",
    name: "Melrose",
    state: "MA",
    region: "Middlesex County",
    distanceMiles: 4,
    isPrimary: false,
    lat: 42.4584,
    lng: -71.0662,
    metaDescription:
      "Landscaping and masonry in Melrose MA. Victorian-home masonry restoration, mortar matching, garden beds and detail-level lawn care. Free estimates.",
    description:
      "Melrose's Victorian housing stock calls for masonry work that matches the original — plus the garden detail those homes deserve.",
    highlights: [
      "Historic-appropriate repointing and mortar matching",
      "Garden bed design and seasonal colour",
      "Stone and brick walkway restoration",
      "Weekly maintenance with a detail-level finish",
    ],
    content: [
      "Melrose has some of the best-preserved Victorian housing in the area, and that raises the bar on masonry repair. The mortar used on these homes is soft by modern standards, and repointing them with a high-strength modern mix traps moisture in the brick and accelerates spalling — the exact opposite of what the repair was meant to do.",
      "We match mortar to the original in strength, colour and joint profile so a repointed section blends into the surrounding wall instead of announcing itself. On a house with this much character, a visible repair line is a real loss.",
      "The gardens around these homes deserve the same attention. Melrose clients tend to care about bed detail — clean spade edges, layered planting with real seasonal interest and mulch kept off the shrub crowns — and our maintenance visits here are built around that finish level rather than just a fast mow.",
    ],
    nearbyAreas: ["Melrose Highlands", "Wyoming", "Cedar Park", "Mount Hood", "Ell Pond"],
  },
  {
    slug: "burlington-ma",
    name: "Burlington",
    state: "MA",
    region: "Middlesex County",
    distanceMiles: 8,
    isPrimary: false,
    lat: 42.5048,
    lng: -71.1956,
    metaDescription:
      "Landscaping and masonry in Burlington MA. Residential and commercial grounds care, patios, driveways and irrigation systems. Free on-site estimates.",
    description:
      "Burlington splits neatly between well-kept residential streets and commercial property — we handle grounds care and hardscape for both.",
    highlights: [
      "Residential and commercial grounds maintenance",
      "Paver patios, walkways and driveways",
      "Irrigation installation and winterizing",
      "Landscape lighting for homes and businesses",
    ],
    content: [
      "Burlington's residential neighbourhoods are consistently well kept, and standards on the street set expectations for everyone on it. Our maintenance work here focuses on the details that separate a maintained lawn from a sharp one: crisp mechanical edging, clean trim lines around beds and foundations, and hard surfaces blown clean before we leave.",
      "The commercial side of town brings different priorities — reliability, tidy sites and work scheduled around business hours. We run those accounts on a fixed cycle so property managers are not chasing us for a date.",
      "Irrigation is a regular request in Burlington, both new installs and rescuing systems that were installed with poor zoning or damaged by frost. We test every zone, map the coverage gaps, and quote what actually needs replacing separately from what only needs tuning.",
    ],
    nearbyAreas: ["Burlington Center", "Mill Pond", "Winnmere", "Meadowbrook", "Grandview"],
  },
  {
    slug: "lexington-ma",
    name: "Lexington",
    state: "MA",
    region: "Middlesex County",
    distanceMiles: 12,
    isPrimary: false,
    lat: 42.443,
    lng: -71.229,
    metaDescription:
      "Landscaping and masonry in Lexington MA. Historic masonry restoration, natural stone walls, terraces and full landscape design. Free written estimates.",
    description:
      "Lexington's historic homes and larger properties call for careful masonry restoration and landscape work built to the same standard.",
    highlights: [
      "Historic masonry restoration and mortar matching",
      "Natural stone walls, patios and terraces",
      "Full-property landscape design",
      "Landscape lighting and irrigation",
    ],
    content: [
      "Lexington properties carry real history, and the masonry on them deserves restoration rather than replacement wherever the original material is still sound. We assess what can be saved first, and only rebuild what genuinely has to be rebuilt.",
      "Mortar matching is central to that. Historic mortar is deliberately softer than modern mix, and matching it in strength, colour and joint profile is what keeps a repair from standing out — and, more importantly, what keeps the brick or stone from taking the damage instead.",
      "The landscape work follows the same logic. Natural stone walls, terraces and steps age into a Lexington property the way block never quite does. Twelve miles from our shop puts Lexington at the outer edge of our regular route, and we schedule accordingly so crews arrive with a full working day ahead of them.",
    ],
    nearbyAreas: ["Lexington Center", "East Lexington", "Countryside", "Meriam Hill", "Follen Hill", "Pine Meadows"],
  },
  {
    slug: "cambridge-ma",
    name: "Cambridge",
    state: "MA",
    region: "Middlesex County",
    distanceMiles: 12,
    isPrimary: false,
    lat: 42.3736,
    lng: -71.1097,
    metaDescription:
      "Landscaping and masonry in Cambridge MA. Courtyard design, historic brick restoration, small-space patios and raised beds. Free on-site estimates.",
    description:
      "Cambridge is small-space work at a high standard: courtyards, roof-level planting and brick restoration on some of the oldest housing around.",
    highlights: [
      "Courtyard and small-space garden design",
      "Historic brick repointing and restoration",
      "Compact patios, paths and raised beds",
      "Container and seasonal planting programmes",
    ],
    content: [
      "Cambridge yards are small, overlooked and often shaded on three sides — which makes them one of the more interesting design problems we get. The answer is rarely more plants; it is usually better structure. A defined patio, a level change, a wall that doubles as seating, and planting chosen for shade tolerance rather than optimism.",
      "The brickwork here is among the oldest we touch. Soft historic mortar and centuries of freeze-thaw mean joints recede and faces spall, and every repointing job starts with matching the original mix rather than reaching for a modern high-strength mortar that will damage the brick around it.",
      "Access shapes every Cambridge quote. Narrow streets, permit parking and shared alleys mean materials are frequently barrowed in by hand and staged over multiple days. We build that into the schedule and the price up front so there are no surprises once the job starts.",
    ],
    nearbyAreas: ["Huron Village", "West Cambridge", "North Cambridge", "Cambridgeport", "Avon Hill", "Agassiz"],
  },
  {
    slug: "somerville-ma",
    name: "Somerville",
    state: "MA",
    region: "Middlesex County",
    distanceMiles: 10,
    isPrimary: false,
    lat: 42.3876,
    lng: -71.0995,
    metaDescription:
      "Landscaping and masonry in Somerville MA. Small yard transformations, brick step rebuilds, compact retaining walls and low-maintenance planting. Free quotes.",
    description:
      "Somerville is dense, sloped and full of tired brick — perfect conditions for small-yard design and step and wall rebuilds.",
    highlights: [
      "Small yard and triple-decker garden design",
      "Brick and concrete step rebuilds",
      "Compact retaining walls on sloped lots",
      "Low-maintenance planting for rental and owner-occupied homes",
    ],
    content: [
      "Somerville packs a lot of house onto a little land, and the yards that come with it are usually narrow strips beside or behind the building. We treat those as rooms rather than leftovers: a small paved area that actually fits a table, a raised bed along one edge, and planting selected for the light the space genuinely gets.",
      "Front steps are a constant here. Decades of freeze-thaw on shallow footings leave brick and concrete steps tipped, cracked and uneven, and uneven risers are the ones people trip on. We rebuild on a proper base below frost depth so the fix does not repeat in five years.",
      "The hills through Winter Hill and Spring Hill mean grade changes on a lot of lots. Compact retaining walls handle those, and even at three feet the drainage detailing behind the wall matters just as much as it does on a much larger structure.",
    ],
    nearbyAreas: ["Spring Hill", "Winter Hill", "Davis Square", "Ten Hills", "West Somerville", "Magoun Square"],
  },
  {
    slug: "danvers-ma",
    name: "Danvers",
    state: "MA",
    region: "Essex County",
    distanceMiles: 11,
    isPrimary: false,
    lat: 42.5751,
    lng: -70.93,
    metaDescription:
      "Landscaping and masonry in Danvers MA. Full landscape installation, paver patios, retaining walls and seasonal lawn maintenance. Free on-site estimates.",
    description:
      "Danvers marks the North Shore end of our route — larger yards, full landscape installs and plenty of hardscape.",
    highlights: [
      "Full landscape installation on larger lots",
      "Paver patios, walkways and driveways",
      "Retaining walls and grade correction",
      "Seasonal clean-ups and lawn maintenance",
    ],
    content: [
      "Danvers sits at the North Shore end of our service area, and the properties here give us room to work. Full landscape installations are common — grading, new lawn, structured planting beds and hardscape planned together rather than added one piece at a time.",
      "Hardscape is a big share of that. Paver patios, walkways connecting the drive to the entry, and retaining walls managing the grade changes that come with larger, less level lots. Every one of them gets full-depth excavation and base compacted in lifts, because in this climate the base is what determines whether the surface is still flat in ten years.",
      "Being eleven miles out, we schedule Danvers work in blocks so crews arrive with a full day ahead of them instead of losing the morning to travel. It keeps projects moving and keeps the pricing honest.",
    ],
    nearbyAreas: ["Danvers Center", "Hathorne", "Putnamville", "Tapleyville", "Danversport", "Burley's Corner"],
  },
];

export const primaryLocation = locations.find((l) => l.isPrimary) ?? locations[0];

export const getLocation = (slug: string) => locations.find((l) => l.slug === slug);
